import { type Theme, type WaterRegion } from '@cuboom/shared';

/**
 * Water rendering — animated wave surface, depth gradient, shore foam and
 * drifting shimmer. Pure visuals: the sim's water regions (plain rects) and
 * the 3-second drown rule are untouched; everything here keys off `nowS`
 * (wall clock) so it never touches determinism.
 */

function withAlpha(hex: string, a: number): string {
  const v = parseInt(hex.slice(1), 16);
  return `rgba(${(v >> 16) & 255}, ${(v >> 8) & 255}, ${v & 255}, ${a})`;
}

/** Deterministic 0..1 for shimmer placement (per region + slot). */
function hashn(n: number): number {
  let t = Math.imul(n | 0, 0x9e3779b9) | 0;
  t = Math.imul(t ^ (t >>> 16), 0x45d9f3b);
  return ((t ^ (t >>> 16)) >>> 0) / 4294967296;
}

/** Water surface height at world x: two crossed sine trains, gently animated. */
function surfaceY(r: WaterRegion, x: number, nowS: number): number {
  const t = x - r.x + r.x * 0.31; // phase varies per region so neighbors differ
  return (
    r.y +
    Math.sin(t * 0.02 + nowS * 1.6) * 2.6 +
    Math.sin(t * 0.045 - nowS * 2.3 + r.x * 0.01) * 1.4
  );
}

export function drawWater(
  ctx: CanvasRenderingContext2D,
  regions: WaterRegion[],
  theme: Theme,
  nowS: number,
): void {
  for (const r of regions) {
    if (r.w <= 0 || r.h <= 0) continue;

    // --- body: wave-shaped top edge, depth gradient fill --------------------
    ctx.beginPath();
    ctx.moveTo(r.x, surfaceY(r, r.x, nowS));
    for (let x = r.x + 6; x < r.x + r.w; x += 6) ctx.lineTo(x, surfaceY(r, x, nowS));
    ctx.lineTo(r.x + r.w, surfaceY(r, r.x + r.w, nowS));
    ctx.lineTo(r.x + r.w, r.y + r.h);
    ctx.lineTo(r.x, r.y + r.h);
    ctx.closePath();

    const grad = ctx.createLinearGradient(0, r.y, 0, r.y + Math.min(r.h, 300));
    grad.addColorStop(0, withAlpha(theme.water, 0.55));
    grad.addColorStop(1, withAlpha(theme.waterDeep, 0.78));
    if (r.h <= 300) {
      ctx.fillStyle = grad;
      ctx.fill();
    } else {
      // Deep body flat, then the gradient band clipped to the wave path.
      ctx.fillStyle = withAlpha(theme.waterDeep, 0.78);
      ctx.fill();
      ctx.save();
      ctx.clip();
      ctx.fillStyle = grad;
      ctx.fillRect(r.x, r.y - 8, r.w, 308);
      ctx.restore();
    }

    // --- crest: bright foam line riding the wave ----------------------------
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(r.x, surfaceY(r, r.x, nowS));
    for (let x = r.x + 6; x < r.x + r.w; x += 6) ctx.lineTo(x, surfaceY(r, x, nowS));
    ctx.stroke();
    // Ghost line just under the crest — the lip of the swell.
    ctx.strokeStyle = 'rgba(255,255,255,0.22)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(r.x, surfaceY(r, r.x, nowS) + 3);
    for (let x = r.x + 6; x < r.x + r.w; x += 6) ctx.lineTo(x, surfaceY(r, x, nowS) + 3);
    ctx.stroke();

    // --- shore foam: wash where the water meets each bank -------------------
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    for (const endX of [r.x + 3, r.x + r.w - 3]) {
      for (let i = 0; i < 3; i++) {
        const bob = Math.sin(nowS * 2.2 + i * 2.1 + r.x) * 2;
        ctx.beginPath();
        ctx.ellipse(
          endX + (endX === r.x + 3 ? i * 4 : -i * 4),
          surfaceY(r, endX, nowS) + 2 + bob + i * 2,
          5 - i,
          2.2,
          0,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
    }

    // --- shimmer: light dashes drifting under the surface --------------------
    ctx.fillStyle = 'rgba(255,255,255,0.16)';
    const span = Math.min(r.h - 12, 70);
    if (span > 6) {
      for (let i = 0; i < 8; i++) {
        const base = hashn(r.x * 7 + i * 13);
        const drift = (nowS * (7 + (i % 3) * 5)) % r.w;
        const dx = r.x + ((base * r.w + drift) % r.w);
        const dy = r.y + 10 + hashn(r.x * 3 + i * 29) * span;
        ctx.fillRect(dx, dy, 12 + (i % 4) * 5, 1.6);
      }
    }
  }
}
